import React from 'react'

export type Theme = 'light' | 'dark'
export type ThemeMode = Theme | 'system'

type ThemeContextValue = {
  mode: ThemeMode
  theme: Theme // effective theme after resolving 'system'
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined)

const STORAGE_KEY = 'nsdev.theme.mode'

function getSystemTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(mode: ThemeMode): Theme {
  return mode === 'system' ? getSystemTheme() : mode
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = React.useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
      if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
    } catch {
    }
    return 'system'
  })

  const theme = resolveTheme(mode)

  // Apply attribute for CSS variables and update when system preference changes (if mode === 'system')
  React.useEffect(() => {
    const el = document.documentElement
    el.setAttribute('data-theme', theme)

    if (mode !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const t = mq.matches ? 'dark' : 'light'
      el.setAttribute('data-theme', t)
    }
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
    }
    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onChange)
      }
    }
  }, [mode, theme])

  const setModePersist = React.useCallback((m: ThemeMode) => {
    setMode(m)
    try {
      localStorage.setItem(STORAGE_KEY, m)
    } catch {
    }
  }, [])

  const value = React.useMemo<ThemeContextValue>(() => ({
    mode,
    theme,
    setMode: setModePersist
  }), [mode, theme, setModePersist])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
